import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Save, Users } from "lucide-react";

export default function TournamentGameRecord() {
  const { tournamentId, gameId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState(null);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [playerStats, setPlayerStats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    api.get(`/tournaments/${tournamentId}/games/${gameId}`)
      .then((r) => {
        if (!alive) return;
        setGameData(r.data);
        setScoreA(r.data.game.score_a || 0);
        setScoreB(r.data.game.score_b || 0);
        setPlayerStats(
          [...r.data.home_team.players, ...r.data.away_team.players].map((player) => ({
            ...player,
            points: 0,
            rebounds_off: 0,
            rebounds_def: 0,
            assists: 0,
            steals: 0,
            blocks: 0,
            turnovers: 0,
            fouls: 0,
            shots: [],
          }))
        );
      })
      .catch((err) => {
        console.error(err);
        toast.error(err?.response?.data?.detail || "Unable to load game details");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, [tournamentId, gameId]);

  const updateStat = (playerId, field, value) => {
    setPlayerStats((prev) => prev.map((p) => p.id === playerId ? { ...p, [field]: value } : p));
  };

  const saveRecord = async () => {
    if (!user) {
      toast.error("You must be signed in to save the game record.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        score_a: Number(scoreA),
        score_b: Number(scoreB),
        status: "completed",
        player_stats: playerStats.map((p) => ({
          user_id: p.id,
          team_id: p.team_id,
          points: Number(p.points) || 0,
          rebounds_off: Number(p.rebounds_off) || 0,
          rebounds_def: Number(p.rebounds_def) || 0,
          assists: Number(p.assists) || 0,
          steals: Number(p.steals) || 0,
          blocks: Number(p.blocks) || 0,
          turnovers: Number(p.turnovers) || 0,
          fouls: Number(p.fouls) || 0,
          shots: p.shots || [],
        })),
      };

      await api.post(`/tournaments/${tournamentId}/games/${gameId}/record`, payload);
      toast.success("Game record saved.");
      navigate(`/tournaments/${tournamentId}/games`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to save game record.");
    } finally {
      setSaving(false);
    }
  };

  const teamRows = useMemo(() => {
    if (!gameData) return [];
    return [
      { team: gameData.home_team, side: "Home" },
      { team: gameData.away_team, side: "Away" },
    ];
  }, [gameData]);

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 py-12 text-zinc-400">Loading game record...</div>;
  }

  if (!gameData) {
    return <div className="max-w-6xl mx-auto px-6 py-12 text-zinc-400">Game not found.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
      <div className="flex items-center justify-between gap-4">
        <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white">← Back</button>
        <div className="text-[10px] uppercase tracking-[0.3em] text-court">Record Game Stats</div>
      </div>

      <div className="border border-zinc-800 p-6 bg-zinc-950">
        <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-court">{gameData.home_team.name} vs {gameData.away_team.name}</div>
        <div className="mt-3 flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400 uppercase tracking-[0.3em]">Score A</span>
            <input type="number" value={scoreA} onChange={(e) => setScoreA(e.target.value)} className="w-24 bg-transparent border border-zinc-800 py-2 px-3 outline-none text-white" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400 uppercase tracking-[0.3em]">Score B</span>
            <input type="number" value={scoreB} onChange={(e) => setScoreB(e.target.value)} className="w-24 bg-transparent border border-zinc-800 py-2 px-3 outline-none text-white" />
          </div>
        </div>
      </div>

      {teamRows.map(({ team, side }) => (
        <div key={team.id} className="border border-zinc-800 p-6">
          <div className="text-[11px] uppercase tracking-[0.3em] text-court">{side} Team</div>
          <div className="font-display font-black text-2xl uppercase mt-2">{team.name}</div>
          <div className="mt-4 space-y-3">
            {team.players.map((player) => {
              const stats = playerStats.find((p) => p.id === player.id);
              return (
                <div key={player.id} className="border border-zinc-800 p-4 rounded-lg bg-zinc-950/50">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-bold text-white">{player.name}</div>
                      <div className="text-xs text-zinc-400">{player.email || player.phone}</div>
                    </div>
                    <div className="text-xs uppercase tracking-[0.25em] text-court">{player.position || "Player"}</div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    {[
                      ["points", "Points"],
                      ["rebounds_off", "OREB"],
                      ["rebounds_def", "DREB"],
                      ["assists", "Ast"],
                      ["steals", "Stl"],
                      ["blocks", "Blk"],
                      ["turnovers", "TO"],
                      ["fouls", "PF"],
                    ].map(([field, label]) => (
                      <label key={field} className="block text-xs text-zinc-400">
                        <span className="block mb-1 uppercase tracking-[0.2em]">{label}</span>
                        <input
                          type="number"
                          value={stats?.[field] ?? 0}
                          min="0"
                          onChange={(e) => updateStat(player.id, field, Number(e.target.value))}
                          className="w-full bg-transparent border border-zinc-800 py-2 px-3 text-white outline-none"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="flex justify-end gap-3">
        <button onClick={() => navigate(-1)} className="h-12 px-5 border border-zinc-800 text-zinc-400 hover:text-white uppercase tracking-[0.25em]">Cancel</button>
        <button onClick={saveRecord} disabled={saving} className="h-12 px-6 bg-court text-black font-bold uppercase tracking-[0.25em] border-b-4 border-court-deep hover:bg-court-hover disabled:opacity-50">
          <Save className="w-4 h-4 inline-block mr-2" /> {saving ? "Saving…" : "Save Record"}
        </button>
      </div>
    </div>
  );
}
